import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import api from "../api/api";
import "../styles/RequesterPage.css";

const RequesterPage = () => {
  const [formData, setFormData] = useState({
    item: "",
    description: "",
    quantity: "",
    units: "",
    cost_centre: "",
    stock_balance: "",
    remarks: "",
    intended_delivery_date: "",
    category: "",
    lot: "",
    location: "",
  });

  const [categories, setCategories] = useState([]);
  const [lots, setLots] = useState([]);
  const [activeLot, setActiveLot] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [openRequests, setOpenRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("form");

  // Bulk upload
  const [bulkData, setBulkData] = useState([]);
  const [bulkPreview, setBulkPreview] = useState(false);
  const [bulkDataFile, setBulkDataFile] = useState(null);
  const [bulkLot, setBulkLot] = useState("");
  const [bulkCategoryAll, setBulkCategoryAll] = useState("");
  const [bulkLocationAll, setBulkLocationAll] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedLots, setExpandedLots] = useState({});

  // Hard-coded locations
  const locations = ["RVR 1", "RVR 2"];

  // ---------- Helpers ----------
  const extractLeadDays = (cat) => {
    return cat && cat.lead_time_days ? parseInt(cat.lead_time_days, 10) : null;
  };

  const computeDateFromLead = (leadDays) => {
    if (!Number.isFinite(leadDays)) return null;
    let target = new Date();
    target.setHours(0, 0, 0, 0);
    let addedDays = 0;
    while (addedDays < leadDays) {
      target.setDate(target.getDate() + 1);
      const day = target.getDay();
      if (day !== 0 && day !== 6) addedDays++;
    }
    return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-${String(target.getDate()).padStart(2, "0")}`;
  };

  // ---------- Fetch Data ----------
  const fetchCategories = async () => {
    try {
      const res = await api.get("item-categories/");
      setCategories(res.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchLots = async () => {
    try {
      const res = await api.get("lots/");
      const allLots = res.data || [];
      setLots(allLots);
      const pending = allLots.find((lot) => lot.status === "PENDING");
      if (pending) setActiveLot(pending);
    } catch (err) {
      console.error("Error fetching lots:", err);
    }
  };

  const fetchOpenRequests = async () => {
    try {
      const res = await api.get("purchase-requests/");
      const requestsWithNames = res.data.map((request) => ({
        ...request,
        location: request.location_name || request.location || "Unknown Location",
      }));
      setOpenRequests(requestsWithNames || []);
    } catch (err) {
      console.error("Error fetching open requests:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchLots();
  }, []);

  useEffect(() => {
    if (activeTab === "requests") fetchOpenRequests();
  }, [activeTab]);

  // ---------- Handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setFormData((prev) => ({ ...prev, category: categoryId }));
    setErrors((prev) => ({ ...prev, category: "" }));

    if (!categoryId) {
      setFormData((prev) => ({ ...prev, intended_delivery_date: "" }));
      return;
    }

    let selected = categories.find((c) => String(c.id) === String(categoryId));
    if (!selected) {
      try {
        const res = await api.get(`item-categories/${categoryId}/`);
        selected = res.data;
      } catch (err) {
        console.error("Error fetching category:", err);
        return;
      }
    }

    const leadDays = extractLeadDays(selected);
    if (!leadDays) {
      Swal.fire("⚠️ Missing Lead Time", "No lead time has been set for this category. Please contact an admin.", "warning");
      return;
    }

    const isoDate = computeDateFromLead(leadDays);
    if (isoDate) {
      setFormData((prev) => ({ ...prev, intended_delivery_date: isoDate }));
      Swal.fire({
        icon: "info",
        title: "Expected Delivery Date Set",
        html: `Lead time: <strong>${leadDays} working day(s)</strong><br/>Expected delivery: <strong>${new Date(isoDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</strong>`,
        confirmButtonColor: "#a92f6f",
      });
    }
  };

  const handleCreateLot = async () => {
    const { value: narration } = await Swal.fire({
      title: "Create New Lot",
      input: "text",
      inputLabel: "Enter short narration (e.g. 'Sep', 'BatchA')",
      showCancelButton: true,
      confirmButtonText: "Generate Lot",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#6b6560",
      inputValidator: (val) => !val && "Narration is required",
    });

    if (!narration) return;

    Swal.fire({ 
      title: "Generating Lot...", 
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
    });

    try {
      const res = await api.post("lots/", { narration });
      await fetchLots();
      setFormData((prev) => ({ ...prev, lot: res.data.id }));
      setBulkLot(res.data.id);
      Swal.fire("✅ Lot Created", `New Lot: ${res.data.full_lot_number}`, "success");
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.narration?.[0] || err.response?.data?.detail || "Failed to create lot. Please try again.";
      Swal.fire("❌ Error", msg, "error");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.item?.trim()) newErrors.item = "Item name is required";
    if (!formData.quantity || Number(formData.quantity) <= 0) newErrors.quantity = "Quantity must be greater than 0";
    if (!formData.units?.trim()) newErrors.units = "Unit of measure is required";
    if (!formData.cost_centre?.trim()) newErrors.cost_centre = "Cost centre is required";
    if (!formData.lot) newErrors.lot = "Please select or create a lot";
    if (!formData.category) newErrors.category = "Please select a category";
    if (!formData.location) newErrors.location = "Please select a location";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const parseBackendErrors = (errorData) => {
    if (!errorData) return "An unexpected error occurred. Please try again.";
    if (typeof errorData === "string") return errorData;
    if (errorData.detail) return errorData.detail;

    const fieldMessages = [];
    const fieldLabels = {
      item: "Item",
      description: "Description",
      quantity: "Quantity",
      units: "Units",
      cost_centre: "Cost Centre",
      stock_balance: "Stock Balance",
      remarks: "Remarks",
      location: "Location",
      category: "Category",
      lot: "Lot",
      intended_delivery_date: "Delivery Date",
    };

    for (const [field, messages] of Object.entries(errorData)) {
      const label = fieldLabels[field] || field;
      const msg = Array.isArray(messages) ? messages.join(", ") : String(messages);
      fieldMessages.push(`<strong>${label}:</strong> ${msg}`);
    }

    return fieldMessages.length > 0
      ? fieldMessages.join("<br/>")
      : "Submission failed. Please check your inputs and try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        html: "Please fill in all required fields before submitting.",
        confirmButtonColor: "#a92f6f",
      });
      return;
    }

    setLoading(true);
    Swal.fire({ 
      title: "Submitting Request...", 
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
    });

    const payload = {
      item: formData.item.trim(),
      description: formData.description.trim(),
      quantity: Number(formData.quantity),
      units: formData.units.trim(),
      cost_centre: formData.cost_centre.trim(),
      stock_balance: formData.stock_balance ? Number(formData.stock_balance) : 0,
      remarks: formData.remarks.trim(),
      intended_delivery_date: formData.intended_delivery_date || null,
      category: formData.category,
      lot: Number(formData.lot),
      location: formData.location,
    };

    try {
      await api.post("purchase-requests/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      Swal.fire({
        icon: "success",
        title: "✅ Request Submitted",
        text: "Your purchase request has been submitted successfully.",
        confirmButtonColor: "#a92f6f",
      });

      setFormData({
        item: "",
        description: "",
        quantity: "",
        units: "",
        cost_centre: "",
        stock_balance: "",
        remarks: "",
        intended_delivery_date: "",
        category: "",
        lot: "",
        location: "",
      });

      fetchOpenRequests();
      setActiveTab("requests");
    } catch (err) {
      console.error(err);
      const errData = err.response?.data;
      const errHtml = parseBackendErrors(errData);
      Swal.fire({
        icon: "error",
        title: "❌ Submission Failed",
        html: errHtml,
        confirmButtonColor: "#a92f6f",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------- BULK UPLOAD ----------
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkDataFile(file);

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet);
    if (!rows.length) return Swal.fire("⚠️", "The uploaded Excel file is empty.", "warning");

    const previewRows = rows.map((r) => ({
      ...r,
      category: "",
      intended_delivery_date: "",
      location: locations[0] || "",
    }));
    setBulkData(previewRows);
    setBulkPreview(true);
    setBulkLocationAll(locations[0] || "");
  };

  const handleBulkCategoryChange = (index, categoryId) => {
    const newData = [...bulkData];
    newData[index].category = categoryId;
    const catObj = categories.find((c) => String(c.id) === String(categoryId));
    newData[index].intended_delivery_date = catObj ? computeDateFromLead(extractLeadDays(catObj)) : "";
    setBulkData(newData);
  };

  const handleBulkCategoryAllChange = (categoryId) => {
    setBulkCategoryAll(categoryId);
    const catObj = categories.find((c) => String(c.id) === String(categoryId));
    const leadDays = extractLeadDays(catObj);
    setBulkData(bulkData.map((r) => ({
      ...r,
      category: categoryId,
      intended_delivery_date: leadDays ? computeDateFromLead(leadDays) : "",
    })));
  };

  const handleBulkLocationChange = (index, loc) => {
    const newData = [...bulkData];
    newData[index].location = loc;
    setBulkData(newData);
  };

  const handleBulkLocationAllChange = (loc) => {
    setBulkLocationAll(loc);
    setBulkData(bulkData.map((r) => ({ ...r, location: loc })));
  };

  const handleBulkSubmit = async () => {
    if (!bulkLot) return Swal.fire("⚠️", "Please select a Lot before submitting.", "warning");
    if (!bulkData.length) return Swal.fire("⚠️", "No data to submit.", "warning");

    const missingCategory = bulkData.some((r) => !r.category);
    const missingLocation = bulkData.some((r) => !r.location);
    if (missingCategory || missingLocation) {
      return Swal.fire("⚠️ Incomplete Rows", `All rows must have a ${missingCategory ? "category" : ""}${missingCategory && missingLocation ? " and " : ""}${missingLocation ? "location" : ""} assigned.`, "warning");
    }

    const formDataObj = new FormData();
    formDataObj.append("file", bulkDataFile);
    formDataObj.append("lot", bulkLot.toString());
    if (bulkCategoryAll) formDataObj.append("category_all", bulkCategoryAll.toString());
    if (bulkLocationAll) formDataObj.append("location_all", bulkLocationAll);

    Swal.fire({
      title: "Submit Bulk Requests?",
      text: `${bulkData.length} item(s) will be uploaded.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit All",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#6b6560",
    }).then(async (r) => {
      if (r.isConfirmed) {
        Swal.fire({ title: "Uploading...", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
        try {
          const res = await api.post("purchase-requests/bulk-upload/", formDataObj, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const { created, errors: rowErrors } = res.data;
          let successHtml = `<strong>${created}</strong> request(s) uploaded successfully.`;
          if (rowErrors?.length) {
            successHtml += `<br/><br/>⚠️ <strong>${rowErrors.length}</strong> row(s) had errors:<br/>`;
            successHtml += rowErrors.map((e) => `Row ${e.row}: ${e.error}`).join("<br/>");
          }
          Swal.fire({ 
            icon: created > 0 ? "success" : "warning", 
            title: "Bulk Upload Complete", 
            html: successHtml,
            confirmButtonColor: "#a92f6f",
          });
          setBulkData([]);
          setBulkDataFile(null);
          setBulkPreview(false);
          setBulkLot("");
          setBulkCategoryAll("");
          setBulkLocationAll("");
          setActiveTab("requests");
          fetchOpenRequests();
        } catch (err) {
          console.error(err);
          const msg = err.response?.data?.error || "Bulk upload failed. Please check your file and try again.";
          Swal.fire("❌ Upload Failed", msg, "error");
        }
      }
    });
  };

  const handleDownloadTemplate = () => {
    const headers = [["Item", "Description", "Quantity", "Units", "Cost Centre", "Stock Balance", "Remarks"]];
    const worksheet = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Template");
    XLSX.writeFile(wb, "Purchase_Request_Template.xlsx");
  };

  // ---------- Render Helpers ----------
  const visibleRequests = openRequests.filter((r) => r.status !== "COLLECTED" && r.status !== "REJECTED");

  const groupRequestsByLot = (requests) => {
    return requests.reduce((acc, request) => {
      const lot = request.lot_full_number || "Unassigned";
      if (!acc[lot]) acc[lot] = [];
      acc[lot].push(request);
      return acc;
    }, {});
  };

  const toggleLot = (lot) => setExpandedLots((prev) => ({ ...prev, [lot]: !prev[lot] }));

  const filteredRequests = visibleRequests.filter((r) => {
    const itemMatch = r.item.toLowerCase().includes(searchTerm.toLowerCase());
    const lotMatch = r.lot_full_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return itemMatch || lotMatch;
  });

  const getStatusClass = (status) => {
    const map = {
      "PENDING": "status-pending",
      "APPROVED": "status-approved",
      "REJECTED": "status-rejected",
      "COLLECTED": "status-collected",
    };
    return map[status] || "status-pending";
  };

  const getStatusIcon = (status) => {
    const map = {
      "PENDING": "⏳",
      "APPROVED": "✅",
      "REJECTED": "❌",
      "COLLECTED": "📦",
    };
    return map[status] || "⏳";
  };

  // ---------- Render ----------
  return (
    <div className="requester-container">
      {/* Tabs */}
      <div className="requester-nav">
        <button 
          className={`nav-btn ${activeTab === "form" ? "active" : ""}`} 
          onClick={() => setActiveTab("form")}
        >
          <span>➕</span> Make Request
        </button>
        <button 
          className={`nav-btn ${activeTab === "requests" ? "active" : ""}`} 
          onClick={() => setActiveTab("requests")}
        >
          <span>📋</span> My Requests
        </button>
      </div>

      {/* FORM TAB */}
      {activeTab === "form" && (
        <div className="requester-card">
          <h1 className="requester-title">New Purchase Request</h1>
          <p className="requester-subtitle">Fill in the details below to create a new purchase request. Fields marked with * are required.</p>

          {/* Bulk Upload */}
          <div className="bulk-upload">
            <div className="upload-controls">
              <label className="upload-label">
                <span>📥</span> Bulk Upload (.xlsx)
                <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} hidden />
              </label>
              <button className="btn-small" onClick={handleDownloadTemplate}>
                <span>📄</span> Download Template
              </button>
            </div>
          </div>

          {/* Bulk Preview */}
          {bulkPreview && bulkData.length > 0 && (
            <div className="bulk-preview">
              <h3>Preview Uploaded Data</h3>

              {/* Lot */}
              <div className="form-group">
                <label>Lot Number <span className="required">*</span></label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select value={bulkLot} onChange={(e) => setBulkLot(e.target.value)}>
                    <option value="">-- Select Lot --</option>
                    {lots.map((lot) => <option key={lot.id} value={lot.id}>{lot.full_lot_number}</option>)}
                  </select>
                  <button type="button" onClick={handleCreateLot} className="btn-small">➕ New Lot</button>
                </div>
              </div>

              {/* Location */}
              <div className="form-group">
                <label>Location <span className="required">*</span></label>
                <select value={bulkLocationAll} onChange={(e) => handleBulkLocationAllChange(e.target.value)}>
                  <option value="">-- Select Location --</option>
                  {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </select>
              </div>

              {/* Category All */}
              <div className="form-group">
                <label>Assign One Category to All <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span></label>
                <select value={bulkCategoryAll} onChange={(e) => handleBulkCategoryAllChange(e.target.value)}>
                  <option value="">-- None --</option>
                  {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>

              {/* Table Preview */}
              <div className="table-responsive">
                <table className="requests-table">
                  <thead>
                    <tr>
                      {["Item","Description","Quantity","Units","Cost Centre","Stock Balance","Remarks","Category","Location","Expected Delivery Date"].map(col => <th key={col}>{col}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkData.map((row, i) => (
                      <tr key={i}>
                        <td>{row.Item || ""}</td>
                        <td>{row.Description || ""}</td>
                        <td>{row.Quantity || ""}</td>
                        <td>{row.Units || ""}</td>
                        <td>{row["Cost Centre"] || ""}</td>
                        <td>{row["Stock Balance"] || ""}</td>
                        <td>{row.Remarks || ""}</td>
                        <td>
                          <select value={row.category || ""} onChange={(e) => handleBulkCategoryChange(i, e.target.value)}>
                            <option value="">-- Select Category --</option>
                            {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                          </select>
                        </td>
                        <td>
                          <select value={row.location || locations[0]} onChange={(e) => handleBulkLocationChange(i, e.target.value)}>
                            {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                          </select>
                        </td>
                        <td>{row.intended_delivery_date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bulk-actions">
                <button className="btn-ghost" onClick={() => setBulkPreview(false)}>❌ Cancel</button>
                <button className="btn-primary" onClick={handleBulkSubmit}>🚀 Submit All</button>
              </div>
            </div>
          )}

          {/* Single Request Form */}
          {!bulkPreview && (
            <form onSubmit={handleSubmit} className="requester-form">
              {/* Lot */}
              <div className="form-group">
                <label>Lot Number <span className="required">*</span></label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select name="lot" value={formData.lot} onChange={handleChange} className={errors.lot ? "error-field" : ""}>
                    <option value="">-- Select Lot --</option>
                    {lots.map((lot) => <option key={lot.id} value={lot.id}>{lot.full_lot_number}</option>)}
                  </select>
                  <button type="button" onClick={handleCreateLot} className="btn-small">➕ New Lot</button>
                </div>
                {errors.lot && <small className="error-text">{errors.lot}</small>}
              </div>

              {/* Location */}
              <div className="form-group">
                <label>Location <span className="required">*</span></label>
                <select name="location" value={formData.location} onChange={handleChange} className={errors.location ? "error-field" : ""}>
                  <option value="">-- Select Location --</option>
                  {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </select>
                {errors.location && <small className="error-text">{errors.location}</small>}
              </div>

              {/* Category */}
              <div className="form-group">
                <label>Category <span className="required">*</span></label>
                <select name="category" value={formData.category} onChange={handleCategoryChange} className={errors.category ? "error-field" : ""}>
                  <option value="">-- Select Category --</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
                {errors.category && <small className="error-text">{errors.category}</small>}
              </div>

              {/* Item */}
              <div className="form-group">
                <label>Item <span className="required">*</span></label>
                <input type="text" name="item" value={formData.item} onChange={handleChange} placeholder="Enter item name..." className={errors.item ? "error-field" : ""} />
                {errors.item && <small className="error-text">{errors.item}</small>}
              </div>

              {/* Quantity */}
              <div className="form-group">
                <label>Quantity <span className="required">*</span></label>
                <input type="number" name="quantity" value={formData.quantity} onChange={handleChange} placeholder="0" min="1" className={errors.quantity ? "error-field" : ""} />
                {errors.quantity && <small className="error-text">{errors.quantity}</small>}
              </div>

              {/* Units */}
              <div className="form-group">
                <label>Units <span className="required">*</span></label>
                <input type="text" name="units" value={formData.units} onChange={handleChange} placeholder="e.g. pcs, kg, liters..." className={errors.units ? "error-field" : ""} />
                {errors.units && <small className="error-text">{errors.units}</small>}
              </div>

              {/* Cost Centre */}
              <div className="form-group">
                <label>Cost Centre <span className="required">*</span></label>
                <input type="text" name="cost_centre" value={formData.cost_centre} onChange={handleChange} placeholder="Enter cost centre..." className={errors.cost_centre ? "error-field" : ""} />
                {errors.cost_centre && <small className="error-text">{errors.cost_centre}</small>}
              </div>

              {/* Stock Balance */}
              <div className="form-group">
                <label>Stock Balance</label>
                <input type="number" name="stock_balance" value={formData.stock_balance} onChange={handleChange} placeholder="0" />
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Enter item description..." rows={3} />
              </div>

              {/* Remarks */}
              <div className="form-group">
                <label>Remarks</label>
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} placeholder="Any additional notes..." rows={2} />
              </div>

              {/* Delivery Date */}
              <div className="form-group">
                <label>Expected Delivery Date</label>
                <input type="date" name="intended_delivery_date" value={formData.intended_delivery_date} readOnly />
                <small className="info-berry">📅 Automatically calculated from category lead time (working days only)</small>
              </div>

              {/* Submit */}
              <div className="form-group" style={{ marginTop: "8px" }}>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? (
                    <><span className="loading-spinner"></span> Submitting...</>
                  ) : (
                    <>🚀 Submit Request</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* REQUESTS TAB */}
      {activeTab === "requests" && (
        <div className="requester-card">
          <h2 className="requester-title">My Active Requests</h2>
          <p className="requester-subtitle">View and track all your purchase requests grouped by lot number.</p>

          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by item or lot number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {filteredRequests.length === 0 ? (
            <div className="no-requests">
              <div className="no-requests-icon">📭</div>
              <p>No active requests found.</p>
            </div>
          ) : (
            Object.entries(groupRequestsByLot(filteredRequests)).map(([lot, requests]) => (
              <div key={lot} className="lot-group">
                <h3 
                  className={`lot-title ${expandedLots[lot] ? "expanded" : ""}`} 
                  onClick={() => toggleLot(lot)}
                >
                  <span className="lot-icon">📦</span>
                  <span>{expandedLots[lot] ? "▼" : "▶"} Lot: {lot}</span>
                  <span style={{ marginLeft: "auto", fontSize: "13px", fontWeight: 500, color: "var(--text-muted)" }}>
                    {requests.length} request{requests.length !== 1 ? "s" : ""}
                  </span>
                </h3>
                {expandedLots[lot] && (
                  <div className="table-responsive">
                    <table className="requests-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Category</th>
                          <th>Location</th>
                          <th>Cost Centre</th>
                          <th>Status</th>
                          <th>Delivery Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map(r => (
                          <tr key={r.id}>
                            <td><strong>{r.item}</strong></td>
                            <td>{r.description || "—"}</td>
                            <td>{r.quantity}</td>
                            <td>{r.units}</td>
                            <td>{r.category_name || "—"}</td>
                            <td>{r.location || "—"}</td>
                            <td>{r.cost_centre}</td>
                            <td>
                              <span className={`status-badge ${getStatusClass(r.status)}`}>
                                <span>{getStatusIcon(r.status)}</span>
                                {r.status}
                              </span>
                            </td>
                            <td>{r.intended_delivery_date ? new Date(r.intended_delivery_date + "T00:00:00").toLocaleDateString("en-GB") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default RequesterPage;